import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Battery, Shield, ArrowLeft, Search, CheckCircle, XCircle, AlertCircle,
  Calendar, Zap, Phone, MessageCircle, Clock, Award
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_energy-havolt/artifacts/x68m0xml_Final%20LOGO.png";

const WarrantyCheck = () => {
  const [serialNumber, setSerialNumber] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const handleCheck = async (e) => {
    e.preventDefault();
    
    if (!serialNumber.trim()) {
      setError("Please enter a serial number");
      return;
    }

    setIsLoading(true);
    setError("");
    setResult(null);

    try {
      const response = await axios.get(`${API}/warranty/check/${serialNumber.trim()}`);
      setResult(response.data);
    } catch (err) {
      setError("Unable to check warranty. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "valid": return "text-[#18C964]";
      case "expired": return "text-red-500";
      case "claimed": return "text-yellow-500";
      default: return "text-zinc-400";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "valid": return <CheckCircle className="h-8 w-8 text-[#18C964]" />;
      case "expired": return <XCircle className="h-8 w-8 text-red-500" />;
      case "claimed": return <AlertCircle className="h-8 w-8 text-yellow-500" />;
      default: return <Shield className="h-8 w-8 text-zinc-500" />;
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case "valid": return "bg-[#18C964]/10 border-[#18C964]/30";
      case "expired": return "bg-red-500/10 border-red-500/30";
      case "claimed": return "bg-yellow-500/10 border-yellow-500/30";
      default: return "bg-zinc-800/50 border-zinc-700";
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B]">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-black/90 backdrop-blur-md border-b border-zinc-800">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Havolt" className="h-12 w-auto" />
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="outline" className="border-zinc-700 text-zinc-300 hover:border-[#18C964] hover:text-[#18C964]">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Home
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-32 pb-20">
        <div className="container mx-auto px-4 md:px-8">
          {/* Page Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 mb-6 border border-zinc-800 text-xs font-mono uppercase tracking-widest text-zinc-400">
              <Shield className="h-4 w-4 text-[#18C964]" />
              Warranty Check
            </span>
            <h1 className="text-4xl md:text-6xl font-heading font-bold uppercase tracking-tight mb-4">
              Check Your <span className="text-[#18C964]">Warranty</span>
            </h1>
            <p className="text-zinc-400 max-w-2xl mx-auto text-lg">
              Enter your battery serial number to view warranty status, specifications, and purchase details.
            </p>
          </motion.div>

          {/* Search Box */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="max-w-xl mx-auto mb-12"
          >
            <form onSubmit={handleCheck} className="bg-havolt-paper/50 border border-zinc-800 p-8">
              <Label className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3 block">
                Battery Serial Number
              </Label>
              <div className="flex gap-3">
                <Input
                  type="text"
                  value={serialNumber}
                  onChange={(e) => setSerialNumber(e.target.value.toUpperCase())}
                  placeholder="e.g., HVT-48V-100AH-001"
                  className="bg-havolt-paper border-zinc-700 h-14 font-mono text-lg uppercase"
                  data-testid="serial-input"
                />
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="bg-[#18C964] text-black font-bold px-8 h-14 hover:bg-[#12A150]"
                  data-testid="check-warranty-btn"
                >
                  {isLoading ? (
                    <span className="animate-pulse">Checking...</span>
                  ) : (
                    <>
                      <Search className="mr-2 h-5 w-5" />
                      Check
                    </>
                  )}
                </Button>
              </div>
              {error && (
                <p className="text-red-500 text-sm mt-3">{error}</p>
              )}
              <p className="text-xs text-zinc-500 mt-3">
                The serial number is printed on the battery label or provided in your purchase documents.
              </p>
            </form>
          </motion.div>

          {/* Results */}
          {result && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-4xl mx-auto"
            >
              {result.found ? (
                <div className="space-y-6">
                  {/* Warranty Status Banner */}
                  <div className={`p-6 border ${getStatusBg(result.warranty_status)} flex items-center gap-4`}>
                    {getStatusIcon(result.warranty_status)}
                    <div className="flex-1">
                      <h2 className={`text-2xl font-heading font-bold uppercase ${getStatusColor(result.warranty_status)}`}>
                        Warranty {result.warranty_status === "valid" ? "Active" : result.warranty_status === "expired" ? "Expired" : "Claimed"}
                      </h2>
                      <p className="text-zinc-400">{result.message}</p>
                    </div>
                    {result.warranty_status === "valid" && (
                      <div className="text-right">
                        <div className="text-3xl font-heading font-bold text-[#18C964]">{result.days_remaining}</div>
                        <div className="text-xs font-mono text-zinc-500">DAYS LEFT</div>
                      </div>
                    )}
                  </div>

                  {/* Battery Details */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Specifications */}
                    <div className="bg-havolt-paper/50 border border-zinc-800 p-6">
                      <h3 className="text-sm font-mono uppercase tracking-widest text-zinc-500 mb-4 flex items-center gap-2">
                        <Battery className="h-4 w-4 text-[#18C964]" />
                        Battery Specifications
                      </h3>
                      
                      <div className="space-y-4">
                        <div className="flex justify-between items-center pb-3 border-b border-zinc-800">
                          <span className="text-zinc-400">Serial Number</span>
                          <span className="font-mono text-white font-bold">{result.serial_number}</span>
                        </div>
                        <div className="flex justify-between items-center pb-3 border-b border-zinc-800">
                          <span className="text-zinc-400">Voltage</span>
                          <span className="font-mono text-[#18C964]">{result.voltage}V</span>
                        </div>
                        <div className="flex justify-between items-center pb-3 border-b border-zinc-800">
                          <span className="text-zinc-400">Capacity</span>
                          <span className="font-mono text-[#18C964]">{result.capacity}Ah</span>
                        </div>
                        {result.application && (
                          <div className="flex justify-between items-center">
                            <span className="text-zinc-400">Application</span>
                            <span className="text-white">{result.application}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Warranty & Purchase Info */}
                    <div className="bg-havolt-paper/50 border border-zinc-800 p-6">
                      <h3 className="text-sm font-mono uppercase tracking-widest text-zinc-500 mb-4 flex items-center gap-2">
                        <Award className="h-4 w-4 text-[#18C964]" />
                        Warranty Information
                      </h3>
                      
                      <div className="space-y-4">
                        <div className="flex justify-between items-center pb-3 border-b border-zinc-800">
                          <span className="text-zinc-400">Customer Name</span>
                          <span className="text-white">{result.customer_name}</span>
                        </div>
                        <div className="flex justify-between items-center pb-3 border-b border-zinc-800">
                          <span className="text-zinc-400 flex items-center gap-2">
                            <Calendar className="h-4 w-4" />
                            Purchase Date
                          </span>
                          <span className="font-mono text-white">{result.purchase_date}</span>
                        </div>
                        <div className="flex justify-between items-center pb-3 border-b border-zinc-800">
                          <span className="text-zinc-400 flex items-center gap-2">
                            <Clock className="h-4 w-4" />
                            Warranty Period
                          </span>
                          <span className="font-mono text-[#18C964]">{result.warranty_years} Years</span>
                        </div>
                        <div className="flex justify-between items-center pb-3 border-b border-zinc-800">
                          <span className="text-zinc-400">Warranty Expiry</span>
                          <span className={`font-mono ${getStatusColor(result.warranty_status)}`}>
                            {result.warranty_expiry}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-zinc-400">Status</span>
                          <span className={`font-mono font-bold uppercase ${getStatusColor(result.warranty_status)}`}>
                            {result.warranty_status}
                          </span>
                        </div>
                      </div>

                      {/* Contact for Warranty Claims */}
                      {result.warranty_status === "valid" && (
                        <div className="mt-6 p-4 bg-[#18C964]/10 border border-[#18C964]/30">
                          <p className="text-sm text-zinc-300 mb-3">
                            Need to claim warranty or have questions?
                          </p>
                          <div className="flex gap-3">
                            <a
                              href={`https://wa.me/919079059738?text=Hi, I need support for my Havolt battery. Serial: ${result.serial_number}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex-1"
                            >
                              <Button className="w-full bg-[#25D366] hover:bg-[#20BD5A] text-white text-sm">
                                <MessageCircle className="mr-2 h-4 w-4" />
                                WhatsApp
                              </Button>
                            </a>
                            <a href="tel:+919079059738" className="flex-1">
                              <Button variant="outline" className="w-full border-zinc-700 text-zinc-300 hover:border-[#18C964] hover:text-[#18C964] text-sm">
                                <Phone className="mr-2 h-4 w-4" />
                                Call
                              </Button>
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                // Not Found
                <div className="bg-havolt-paper/50 border border-zinc-800 p-12 text-center">
                  <div className="w-20 h-20 bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-6">
                    <XCircle className="h-10 w-10 text-zinc-600" />
                  </div>
                  <h2 className="text-2xl font-heading font-bold text-zinc-400 mb-2">
                    Battery Not Found
                  </h2>
                  <p className="text-zinc-500 max-w-md mx-auto mb-6">
                    {result.message}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button
                      onClick={() => {
                        setSerialNumber("");
                        setResult(null);
                      }}
                      variant="outline"
                      className="border-zinc-700 text-zinc-300"
                    >
                      Try Again
                    </Button>
                    <a
                      href="https://wa.me/919079059738?text=Hi, I need help finding my battery warranty information"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button className="bg-[#18C964] text-black font-bold">
                        <MessageCircle className="mr-2 h-4 w-4" />
                        Contact Support
                      </Button>
                    </a>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Info Section */}
          {!result && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="max-w-4xl mx-auto mt-12"
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-havolt-paper/50 border border-zinc-800 p-6 text-center">
                  <Shield className="h-10 w-10 text-[#18C964] mx-auto mb-4" />
                  <h3 className="font-heading font-bold uppercase mb-2">3 Year Warranty</h3>
                  <p className="text-sm text-zinc-400">
                    All Havolt batteries come with comprehensive warranty coverage.
                  </p>
                </div>
                <div className="bg-havolt-paper/50 border border-zinc-800 p-6 text-center">
                  <CheckCircle className="h-10 w-10 text-[#18C964] mx-auto mb-4" />
                  <h3 className="font-heading font-bold uppercase mb-2">Easy Claims</h3>
                  <p className="text-sm text-zinc-400">
                    Simple warranty claim process with quick resolution.
                  </p>
                </div>
                <div className="bg-havolt-paper/50 border border-zinc-800 p-6 text-center">
                  <Phone className="h-10 w-10 text-[#18C964] mx-auto mb-4" />
                  <h3 className="font-heading font-bold uppercase mb-2">24/7 Support</h3>
                  <p className="text-sm text-zinc-400">
                    Our team is always ready to help with any issues.
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 border-t border-zinc-800">
        <div className="container mx-auto px-4 md:px-8 text-center">
          <p className="text-sm text-zinc-500">
            © 2024 Hari Om Enterprises. All rights reserved. |{" "}
            <a href="https://havoltbattery.com" className="text-[#18C964] hover:underline">
              havoltbattery.com
            </a>
          </p>
        </div>
      </footer>

      {/* WhatsApp Float */}
      <a
        href="https://wa.me/919079059738?text=Hi, I need help with my battery warranty"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform z-50"
        aria-label="Chat on WhatsApp"
      >
        <MessageCircle className="h-7 w-7 text-white" />
      </a>
    </div>
  );
};

export default WarrantyCheck;
